import { DocumentType, index, modelOptions, mongoose, post, pre, prop } from "@typegoose/typegoose";
import { z } from "zod";
import {
    getRegexToMatchNegativeVersionSelectorFromCompleteDeviceVersion,
    getRegexToMatchPositiveVersionSelectorFromCompleteDeviceVersion,
} from "../../utils/verdiDeviceVersionRegexUtilities.js";
import { ObjectIdString, StoredVerdiIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../zod-decorator/index.js";
import { CompleteDeviceVersion } from "../commonSubSchemas/completeDeviceVersion.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    DeviceVersionRecordFieldsWithDefaults,
    DeviceVersionRecordSchemaTypes,
} from "../types/deviceVersionRecord.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "deviceVersions" as const;

const populateRegexBeforeValidate = async function populateDeviceVersionRecordRegexBeforeValidate(
    this: DocumentType<DeviceVersionRecord>,
) {
    const version = this.version as DocumentType<CompleteDeviceVersion> | null | undefined;
    if (version === undefined || version === null || version.validateSync() !== undefined) {
        return;
    }
    try {
        DeviceVersionRecord.utilities.populateRegex(this);
    } catch (error) {
        this.invalidate("version", error instanceof Error ? error.message : String(error), this.version);
    }
};

const populateRegexBeforeSave = async function populateDeviceVersionRecordRegexBeforeSave(
    this: DocumentType<DeviceVersionRecord>,
) {
    DeviceVersionRecord.utilities.populateRegex(this);
};

/**
 * The complete device version reported by an asset, keyed on its verdiId. SCD Type-2: each version change is a new
 * version of the asset's record.
 */
@pre<DeviceVersionRecord>("save", populateRegexBeforeSave)
@pre<DeviceVersionRecord>("validate", populateRegexBeforeValidate)
@index({ verdiId: 1 }, { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "deviceVersions" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<DeviceVersionRecord>>(...addSchemaKeyHook(SCHEMA_KEY))
export class DeviceVersionRecord extends ScdDocument<DeviceVersionRecord, DeviceVersionRecordFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: StoredVerdiIdString, responseOnly: true })
    @prop({ type: () => String, required: true, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public verdiId!: string;

    @validate({ childClass: CompleteDeviceVersion, responseOnly: true })
    @prop({ type: () => CompleteDeviceVersion, required: true })
    public version!: CompleteDeviceVersion;

    // The regex fields are deliberately `required: true` with `?`, as on DeviceVersion: the hooks fill them before
    // validation. Do not change them to `!`: private `!` drops them from typegoose's document type, and public `!`
    // makes every createScdDocument caller pass them.

    /** Matches complete-version selectors that include this record's `version`; query with `$options: "i"`. */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: true })
    private _regexToMatchPositiveVersionSelector?: string;

    /** Matches complete-version selectors that exclude this record's `version`; query with `$options: "i"`. */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: true })
    private _regexToMatchNegativeVersionSelector?: string;

    static getters = {} as const satisfies Record<string, (self: DeviceVersionRecord) => any>;

    static utilities = {
        /** Throws on a malformed version. */
        populateRegex: (deviceVersionRecord: DeviceVersionRecord): void => {
            if (deviceVersionRecord.version === undefined) {
                return;
            }
            const positiveVersionSelector = getRegexToMatchPositiveVersionSelectorFromCompleteDeviceVersion(
                deviceVersionRecord.version,
            );
            const negativeVersionSelector = getRegexToMatchNegativeVersionSelectorFromCompleteDeviceVersion(
                deviceVersionRecord.version,
            );

            if (positiveVersionSelector !== deviceVersionRecord._regexToMatchPositiveVersionSelector) {
                deviceVersionRecord._regexToMatchPositiveVersionSelector = positiveVersionSelector;
            }
            if (negativeVersionSelector !== deviceVersionRecord._regexToMatchNegativeVersionSelector) {
                deviceVersionRecord._regexToMatchNegativeVersionSelector = negativeVersionSelector;
            }
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<DeviceVersionRecordSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<DeviceVersionRecord, DeviceVersionRecordFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

DeviceVersionRecord.dtoValidators = getZodSchemaFromClass(
    DeviceVersionRecord,
) as unknown as DtoValidatorTypes<DeviceVersionRecordSchemaTypes>;
